/**
 * DefaultTimezoneCard — org-wide default timezone stored on the single
 * `ahoy_default_timezone` property of the `ahoy_org` entity. Admins pick a
 * zone and Save; non-admins see a read-only view. Absent property means unset
 * (placeholder, no preselected UTC).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useUser } from '@/allauth_auth/hooks.js';
import { useOrg } from '@/contexts/OrgContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/lib/base/buttons/button';
import { createEventBuilder } from '@/lib/crm/core/EventBuilder';
import type { EntityTypeName } from '@/lib/crm/core/types';
import { useSubmitEvents } from '@/lib/crm/query/mutations';
import { useCrmObject } from '@/lib/crm/store';
import { logger } from '@/lib/logger';
import { COMMON_TIMEZONES } from '@/lib/scheduling/timezone';
import type { CrmObject } from '@/types/crm';

import { SettingsCard } from '../SettingsCard';
import { TimezonePicker } from '../TimezonePicker';
import { useMountedOrgGuard } from '../use-mounted-org-guard';

const DEFAULT_TIMEZONE_PROPERTY = 'ahoy_default_timezone';

const TIMEZONE_LABELS = new Map(
  COMMON_TIMEZONES.map(tz => [tz.value, tz.label])
);

function readSavedTimezone(object: CrmObject | undefined): string | null {
  const raw = object?.properties?.[DEFAULT_TIMEZONE_PROPERTY]?.value;
  if (typeof raw === 'string' && raw.trim() !== '') {
    return raw;
  }
  return null;
}

function displayTimezone(value: string): string {
  return TIMEZONE_LABELS.get(value) ?? value.replace(/_/g, ' ');
}

export function DefaultTimezoneCard() {
  const { currentOrg } = useOrg();
  const user = useUser();
  const { hasPermission } = usePermissions();
  // Derive during render — hasPermission has unstable identity and must never
  // enter a dep array.
  const canEdit = hasPermission('ahoy_org', 'ahoy_property_updated');
  const orgEntity = useCrmObject(
    currentOrg?.id ?? 0,
    'ahoy_org' as EntityTypeName,
    currentOrg?.entityId ?? undefined
  );
  const { mutateAsync: submitEvents, isPending: isSaving } = useSubmitEvents(
    currentOrg?.id ?? 0,
    'ahoy_org' as EntityTypeName
  );
  const { isMountedRef, currentOrgIdRef } = useMountedOrgGuard(currentOrg?.id);

  const savedFromStore = readSavedTimezone(orgEntity.object);

  const [selectedTimezone, setSelectedTimezone] = useState<string | null>(null);
  const [savedTimezone, setSavedTimezone] = useState<string | null>(null);
  const initializedOrgRef = useRef<number | null>(null);

  // savedTimezone always tracks canonical CRM state; selectedTimezone is only
  // re-initialized on org change so an in-progress pick survives a refetch.
  useEffect(() => {
    if (orgEntity.object) {
      setSavedTimezone(savedFromStore);
      const orgId = currentOrg?.id ?? null;
      if (initializedOrgRef.current !== orgId) {
        setSelectedTimezone(savedFromStore);
        initializedOrgRef.current = orgId;
      }
    }
  }, [orgEntity.object, currentOrg?.id, savedFromStore]);

  const handleChange = useCallback((next: string | null) => {
    setSelectedTimezone(next);
  }, []);

  const hasChanges = selectedTimezone !== savedTimezone;

  const handleSave = useCallback(async () => {
    if (!orgEntity.object || !canEdit || selectedTimezone === null) {
      return;
    }
    const userId = user?.id;
    if (userId == null || userId === '') {
      toast.error('You must be signed in to change this setting');
      return;
    }
    const requestOrgId = currentOrg?.id;
    try {
      const builder = createEventBuilder({
        sourceType: 'ahoy_user',
        sourceId: String(userId),
        startSeq: orgEntity.object.last_event_seq,
      });
      builder.propertiesUpdated(orgEntity.object.entity_id, 'ahoy_org', {
        [DEFAULT_TIMEZONE_PROPERTY]: selectedTimezone,
      });
      await submitEvents({ events: builder.build() });
      if (isMountedRef.current && currentOrgIdRef.current === requestOrgId) {
        setSavedTimezone(selectedTimezone);
        toast.success('Default timezone saved');
      }
    } catch (err) {
      logger.error(
        'Failed to save default timezone',
        err instanceof Error ? err : new Error(String(err)),
        { org_id: requestOrgId }
      );
      if (isMountedRef.current && currentOrgIdRef.current === requestOrgId) {
        setSelectedTimezone(savedTimezone);
        toast.error("We couldn't save the default timezone. Try again.");
      }
    }
  }, [
    orgEntity.object,
    canEdit,
    selectedTimezone,
    savedTimezone,
    submitEvents,
    user,
    currentOrg?.id,
    isMountedRef,
    currentOrgIdRef,
  ]);

  if (!currentOrg?.entityId) {
    return null;
  }

  if (orgEntity.error) {
    return (
      <SettingsCard>
        <h3 className='text-lg font-semibold text-fg-primary'>Timezone</h3>
        <p className='mt-0.5 text-sm text-error-primary'>
          Couldn&apos;t load this setting. Refresh to try again.
        </p>
      </SettingsCard>
    );
  }

  if (orgEntity.isLoading || !orgEntity.object) {
    return (
      <SettingsCard>
        <h3 className='text-lg font-semibold text-fg-primary'>Timezone</h3>
        <p className='mt-0.5 text-sm text-fg-tertiary'>Loading&hellip;</p>
      </SettingsCard>
    );
  }

  return (
    <SettingsCard>
      <h3 className='text-lg font-semibold text-fg-primary'>Timezone</h3>
      <p className='mt-0.5 text-sm text-fg-tertiary'>
        Set a default timezone for your workspace. Agents use it when a member
        hasn&apos;t set their own timezone.
      </p>
      <div className='mt-4'>
        {canEdit ? (
          <>
            <TimezonePicker
              label='Default timezone'
              value={selectedTimezone}
              onChange={handleChange}
              isDisabled={isSaving}
              size='sm'
            />
            <div className='mt-4 flex justify-end'>
              <Button
                color='primary'
                size='md'
                onClick={handleSave}
                isDisabled={
                  !hasChanges || selectedTimezone === null || isSaving
                }
                isLoading={isSaving}
              >
                Save changes
              </Button>
            </div>
          </>
        ) : (
          <>
            {savedTimezone ? (
              <p className='text-sm text-fg-primary'>
                {displayTimezone(savedTimezone)}
              </p>
            ) : (
              <p className='text-sm text-fg-tertiary'>
                No default timezone has been set.
              </p>
            )}
            <p className='mt-2 text-sm text-fg-tertiary'>
              Only account admins can change this setting.
            </p>
          </>
        )}
      </div>
    </SettingsCard>
  );
}
